/**
 * ChangeOrderDocumentView (WU-14) — renders the FROZEN change order document
 * verbatim. Branding, prices, and contract totals come only from the
 * document snapshot the client was sent (hash-bound on accept); nothing is
 * recomputed here. Public tokens cannot read logo files, so the masthead
 * uses the company monogram, as the proposal viewer does.
 */
import React from 'react';
import { BRAND } from '@buildledger/shared';
import type { ChangeOrderDocument } from '@buildledger/shared';

import { fmtDate, fmtMoney, fmtQty } from './format';

const PRE_WRAP: React.CSSProperties = { whiteSpace: 'pre-wrap' };

function scheduleImpactLabel(days: number | null): string {
  if (days === null) return 'Not stated';
  if (days === 0) return 'No change to the schedule';
  const n = Math.abs(days);
  const unit = n === 1 ? 'day' : 'days';
  return days > 0 ? `Adds ${n} ${unit}` : `Shortens the schedule by ${n} ${unit}`;
}

export function ChangeOrderDocumentView({ document }: { document: ChangeOrderDocument }): JSX.Element {
  const { company, client, estimate, changeOrder, contract } = document;

  return (
    <article className="doc" data-testid="co-document">
      <header className="masthead">
        <div className="masthead-brand">
          <span className="monogram" aria-hidden>
            {company.name.slice(0, 1).toUpperCase()}
          </span>
        </div>
        <div className="who">
          <div className="company-name">{company.name}</div>
          {company.licenseLine === null && company.phone === null && company.email === null ? null : (
            <div className="licenses">
              {company.licenseLine === null ? null : <span>{company.licenseLine}</span>}
              {company.phone === null ? null : <span>{company.phone}</span>}
              {company.email === null ? null : <span>{company.email}</span>}
              {company.licenseLine === null ? null : (
                <span className="muted">License information as entered by the contractor.</span>
              )}
            </div>
          )}
        </div>
      </header>

      <div className="cover-title">
        <h1>{changeOrder.title}</h1>
        <div className="cover-subtitle">Change order {changeOrder.number}</div>
      </div>

      <div className="parties">
        <div className="party">
          <span className="party-label">Prepared for</span>
          <span className="party-name">{client.name}</span>
          {client.contactName === null ? null : <span>{client.contactName}</span>}
        </div>
        <div className="party">
          <span className="party-label">Changes estimate</span>
          <span className="party-name">{estimate.number}</span>
          <span>{estimate.title}</span>
          <span>
            Original total <span className="num">{fmtMoney(estimate.originalTotal)}</span>
          </span>
          <span>Issued {fmtDate(document.issuedAt)}</span>
        </div>
      </div>

      {changeOrder.description.trim() === '' ? null : (
        <section className="scope">
          <h2>Description of the change</h2>
          <p style={PRE_WRAP}>{changeOrder.description}</p>
        </section>
      )}

      {changeOrder.reason.trim() === '' ? null : (
        <section className="scope">
          <h2>Reason</h2>
          <p style={PRE_WRAP}>{changeOrder.reason}</p>
        </section>
      )}

      <section className="scope">
        <h2>Schedule impact</h2>
        <p>{scheduleImpactLabel(changeOrder.scheduleImpactDays)}</p>
      </section>

      <section className="pricing">
        <h2>Pricing</h2>
        {changeOrder.lines.length === 0 ? (
          <p className="muted">No line items.</p>
        ) : (
          <table className="price-table">
            <thead>
              <tr>
                <th className="c-desc">Item</th>
                <th className="c-qty">Qty</th>
                <th className="c-unit">Unit</th>
                <th className="c-price">Unit price</th>
                <th className="c-price">Amount</th>
              </tr>
            </thead>
            <tbody>
              {changeOrder.lines.map((line, i) => (
                <tr key={i}>
                  <td className="c-desc">
                    {line.description}
                    {line.taxable ? null : <em className="muted"> (not taxed)</em>}
                  </td>
                  <td className="c-qty num">{fmtQty(line.qty)}</td>
                  <td className="c-unit">{line.unit ?? ''}</td>
                  <td className="c-price num">{fmtMoney(line.unitPrice)}</td>
                  <td className="c-price num">{fmtMoney(line.extended)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="totals">
          <div className="total-row">
            <span>Subtotal</span>
            <span className="num">{fmtMoney(changeOrder.subtotal)}</span>
          </div>
          <div className="total-row">
            <span>Tax ({fmtQty(changeOrder.taxRatePct)}%)</span>
            <span className="num">{fmtMoney(changeOrder.tax)}</span>
          </div>
          <div className="total-row grand">
            <span>Change order total</span>
            <span className="num">{fmtMoney(changeOrder.total)}</span>
          </div>
        </div>
      </section>

      <section className="pricing">
        <h2>Contract</h2>
        <div className="totals">
          <div className="total-row">
            <span>Original contract total</span>
            <span className="num">{fmtMoney(contract.originalTotal)}</span>
          </div>
          <div className="total-row">
            <span>Previously approved changes</span>
            <span className="num">{fmtMoney(contract.previouslyApprovedChanges)}</span>
          </div>
          <div className="total-row">
            <span>This change order</span>
            <span className="num">{fmtMoney(changeOrder.total)}</span>
          </div>
          <div className="total-row grand">
            <span>Revised contract total if accepted (as of {fmtDate(document.issuedAt)})</span>
            <span className="num">{fmtMoney(contract.revisedTotalIfAccepted)}</span>
          </div>
        </div>
        <p className="muted">Based on changes approved when this change order was issued.</p>
      </section>

      <p className="boundary-note">{document.boundaryNote}</p>

      <footer className="doc-footer">
        <span>{company.name}</span>
        <span>
          Change order {changeOrder.number} · {estimate.number} · issued{' '}
          {fmtDate(document.issuedAt)} · generated with {BRAND.name}
        </span>
      </footer>
    </article>
  );
}
